import { CellTagList } from 'components/common';
import { OrderHeaders } from 'configurations/OrderEntity/OrderHeaders';
// TODO: Update import from core index (can't read default from undefined)
// import { OrderHeaders, ShipmentHeaders } from 'configurations';
import { ShipmentHeaders } from 'configurations/ShipmentEntity/ShipmentHeaders';
import deepEqual from 'fast-deep-equal';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ViewContext } from 'utils/context';
import {
  IDisplayOptions,
  TActiveColumn,
} from 'utils/context/ViewContext/types';
import { usePermissions } from 'utils/hooks';

import { ShipmentEntity } from '@project44-models/shipment-list';

interface IColumnContext {
  hasColumnsModified: boolean;
  updateColumns: (displayOptions: IDisplayOptions) => void;
  viewColumns: TActiveColumn[];
}

const ColumnContext = createContext<IColumnContext>({
  hasColumnsModified: false,
  updateColumns: () => null,
  viewColumns: [],
});

interface IColumnContextProvider {
  children: React.ReactNode;
}

function ColumnContextProvider({ children }: IColumnContextProvider) {
  const {
    savedOrders,
    savedShipments,
    selectedView,
    viewType,
    setAllDisplayOptions,
    setWillSearch,
  } = useContext(ViewContext);
  const permissions = usePermissions();
  const isOrder = viewType === 'order';

  const [orderColumns, setOrderColumns] = useState(OrderHeaders);
  const [shipmentColumns, setShipmentColumns] = useState(ShipmentHeaders);
  const [allOrderView, allShipmentView, allPage] = useMemo(
    () => [
      ['all-orders'].includes(selectedView?.id || ''),
      ['all-shipments'].includes(selectedView?.id || ''),
      ['all-orders', 'all-shipments'].includes(selectedView?.id || ''),
    ],
    [selectedView],
  );

  const viewColumns = useMemo(() => {
    const defaultColumns = (
      isOrder ? orderColumns : shipmentColumns
    ) as TActiveColumn[];
    const active = [...(selectedView.displayOptions?.activeColumns || [])];
    const ids = active.map(({ id }) => id);
    defaultColumns.forEach((column) => {
      if (!ids.includes(column.id)) active.push(column);
    });
    return active
      .map((column) => ({
        ...defaultColumns.find(({ id }) => id === column.id),
        ...column,
      }))
      .filter(({ id }) => {
        if (id === 'related-orders')
          return permissions.permissions?.canViewOrders;
        return true;
      })
      .map((col) => {
        // TODO FIX Reference Keys are specific to Shipments only
        if (/ref-/i.test(col.id)) {
          return {
            ...col,
            element: ({ customAttributes = [] }: ShipmentEntity) => {
              const attr = customAttributes.find(
                ({ name }) => name === col.label,
              );
              return <CellTagList namespace={col.label} data={attr?.values} />;
            },
          };
        }
        return col;
      });
  }, [orderColumns, selectedView, shipmentColumns, isOrder, permissions]);

  const hasColumnsModified = useMemo(() => {
    const [columnActive, columnDefaults] = [
      isOrder ? orderColumns : shipmentColumns,
      isOrder ? OrderHeaders : ShipmentHeaders,
    ];
    return !deepEqual(columnActive, columnDefaults);
  }, [orderColumns, shipmentColumns, isOrder]);

  const updateColumns = useCallback(
    async (next: IDisplayOptions) => {
      if (allShipmentView) {
        setShipmentColumns(next.activeColumns);
        setAllDisplayOptions(next);
      } else if (allOrderView) {
        setOrderColumns(next.activeColumns);
      } else {
        if (selectedView) {
          const { updateView } = isOrder ? savedOrders : savedShipments;
          await updateView(selectedView?.id || '', {
            ...selectedView,
            displayOptions: next,
          });
        }
      }
      setWillSearch();
    },
    [
      allOrderView,
      allShipmentView,
      isOrder,
      savedOrders,
      savedShipments,
      selectedView,
      setAllDisplayOptions,
      setWillSearch,
    ],
  );

  useEffect(() => {
    if (allPage) {
      setShipmentColumns(ShipmentHeaders);
      setOrderColumns(OrderHeaders);
    }
  }, [allPage, selectedView]);

  const value = useMemo(
    () => ({ hasColumnsModified, updateColumns, viewColumns }),
    [hasColumnsModified, updateColumns, viewColumns],
  );

  return (
    <ColumnContext.Provider value={value}>{children}</ColumnContext.Provider>
  );
}

export { ColumnContext, ColumnContextProvider };
